import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  InputAdornment,
} from "@mui/material";
import { DraggablePaper } from "./DraggablePaper";
import { useDebouncedCallback } from "../utils/useDebouncedCallback";

// Memoize MoveDraggable to prevent unnecessary re-renders
const MoveDraggable = React.memo(
  ({ open, onClose, canvas, selectedObject }) => {
    const [left, setLeft] = useState(0);
    const [top, setTop] = useState(0);
    const [prevLeft, setPrevLeft] = useState(0);
    const [prevTop, setPrevTop] = useState(0);
    const [isDirty, setIsDirty] = useState(false);
    const [inputLeft, setInputLeft] = useState("");
    const [inputTop, setInputTop] = useState("");
    const selectedObjectRef = useRef(selectedObject);

    // Update ref when selectedObject changes
    useEffect(() => {
      selectedObjectRef.current = selectedObject;
      console.log("[MoveDraggable] selectedObject updated:", selectedObject);
    }, [selectedObject]);

    // Initialize position values when dialog opens or selected object changes
    useEffect(() => {
      if (selectedObject && open) {
        const currentLeft = Math.round(Number(selectedObject.get("left")) || 0);
        const currentTop = Math.round(Number(selectedObject.get("top")) || 0);
        setLeft(currentLeft);
        setTop(currentTop);
        setInputLeft(currentLeft.toString());
        setInputTop(currentTop.toString());
        setPrevLeft(currentLeft);
        setPrevTop(currentTop);
        setIsDirty(false);
        console.log(
          "[MoveDraggable] Initialized left:",
          currentLeft,
          "top:",
          currentTop
        );
      }
    }, [selectedObject, open]);

    // Memoize update callbacks
    const updateLeftCallback = useCallback(
      (value) => {
        const roundedValue = Number(value);
        setLeft(roundedValue);
        setInputLeft(roundedValue.toString());
        if (selectedObjectRef.current && canvas) {
          canvas.setActiveObject(selectedObjectRef.current);
          selectedObjectRef.current.set({ left: roundedValue });
          selectedObjectRef.current.setCoords();
          canvas.requestRenderAll();
          setIsDirty(true);
          console.log(
            "[MoveDraggable] Updated left:",
            roundedValue,
            "Object:",
            selectedObjectRef.current
          );
        } else {
          console.warn("[MoveDraggable] Update failed:", {
            selectedObject: selectedObjectRef.current,
            canvas,
          });
        }
      },
      [canvas]
    );

    const updateTopCallback = useCallback(
      (value) => {
        const roundedValue = Number(value);
        setTop(roundedValue);
        setInputTop(roundedValue.toString());
        if (selectedObjectRef.current && canvas) {
          canvas.setActiveObject(selectedObjectRef.current);
          selectedObjectRef.current.set({ top: roundedValue });
          selectedObjectRef.current.setCoords();
          canvas.requestRenderAll();
          setIsDirty(true);
          console.log(
            "[MoveDraggable] Updated top:",
            roundedValue,
            "Object:",
            selectedObjectRef.current
          );
        } else {
          console.warn("[MoveDraggable] Update failed:", {
            selectedObject: selectedObjectRef.current,
            canvas,
          });
        }
      },
      [canvas]
    );

    // Create debounced update functions
    const updateLeft = useDebouncedCallback(updateLeftCallback, 100, canvas);
    const updateTop = useDebouncedCallback(updateTopCallback, 100, canvas);

    // Handle dialog close (cancel)
    const handleCancel = useCallback(() => {
      if (isDirty && selectedObject) {
        selectedObject.set({ left: prevLeft, top: prevTop });
        selectedObject.setCoords();
        canvas?.requestRenderAll();
        console.log(
          "[MoveDraggable] Canceled, reverted to left:",
          prevLeft,
          "top:",
          prevTop
        );
      }
      onClose();
    }, [isDirty, selectedObject, prevLeft, prevTop, canvas, onClose]);

    // Handle apply position (save changes and add to history)
    const handleApply = useCallback(() => {
      updateLeft.flush();
      updateTop.flush();
      if (isDirty && canvas && selectedObject) {
        canvas.setActiveObject(selectedObject);
        selectedObject.set({ left, top });
        selectedObject.setCoords();
        canvas.requestRenderAll();
        canvas.fire("object:modified", { target: selectedObject });
        console.log(
          "[MoveDraggable] Applied left:",
          left,
          "top:",
          top,
          "Fired object:modified for:",
          selectedObject
        );
      } else {
        console.log(
          "[MoveDraggable] Apply skipped: isDirty=",
          isDirty,
          "canvas=",
          !!canvas,
          "selectedObject=",
          !!selectedObject
        );
      }
      setIsDirty(false);
      onClose();
    }, [
      isDirty,
      canvas,
      selectedObject,
      left,
      top,
      updateLeft,
      updateTop,
      onClose,
    ]);

    // Stop mouse events from propagating to allow input interaction
    const stopPropagation = useCallback((e) => {
      e.stopPropagation();
    }, []);

    // Prevent backdrop click from closing the dialog
    const handleBackdropClick = useCallback((e) => {
      e.stopPropagation();
    }, []);

    // Prevent keydown events (e.g., Escape key) from closing the dialog
    const handleKeyDown = useCallback((e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
      }
    }, []);

    // Handle input changes
    const handleLeftChange = useCallback((e) => {
      setInputLeft(e.target.value);
    }, []);

    const handleLeftBlur = useCallback(() => {
      const value = Number(inputLeft);
      if (!isNaN(value)) {
        updateLeft(value);
      } else {
        setInputLeft(left.toString());
      }
    }, [inputLeft, left, updateLeft]);

    const handleTopChange = useCallback((e) => {
      setInputTop(e.target.value);
    }, []);

    const handleTopBlur = useCallback(() => {
      const value = Number(inputTop);
      if (!isNaN(value)) {
        updateTop(value);
      } else {
        setInputTop(top.toString());
      }
    }, [inputTop, top, updateTop]);

    const handleLeftStep = useCallback(
      (e) => {
        const value = Number(e.target.value);
        if (!isNaN(value)) {
          updateLeft(value);
        }
      },
      [updateLeft]
    );

    const handleTopStep = useCallback(
      (e) => {
        const value = Number(e.target.value);
        if (!isNaN(value)) {
          updateTop(value);
        }
      },
      [updateTop]
    );

    return (
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          handleCancel();
        }}
        PaperComponent={DraggablePaper}
        aria-labelledby="draggable-dialog-title"
        BackdropProps={{
          style: { backgroundColor: "rgba(0, 0, 0, 0.2)" },
          onClick: handleBackdropClick,
        }}
        onKeyDown={handleKeyDown}
        sx={{
          "& .MuiDialog-paper": {
            backgroundColor: "#fff",
            boxShadow: "0px 3px 5px rgba(0, 0, 0, 0.2)",
          },
        }}
      >
        <DialogTitle
          id="draggable-dialog-title"
          sx={{
            color: "#ffffff",
            backgroundColor: "#0d2445",
            fontSize: "20px",
            display: "flex",
            alignItems: "center",
            cursor: "move",
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ marginRight: "8px" }}
          >
            <circle cx="4" cy="4" r="1.5" fill="#ffffff" />
            <circle cx="8" cy="4" r="1.5" fill="#ffffff" />
            <circle cx="4" cy="8" r="1.5" fill="#ffffff" />
            <circle cx="8" cy="8" r="1.5" fill="#ffffff" />
            <circle cx="4" cy="12" r="1.5" fill="#ffffff" />
            <circle cx="8" cy="12" r="1.5" fill="#ffffff" />
          </svg>
          Move Toolbar
        </DialogTitle>
        <DialogContent
          onMouseDown={stopPropagation}
          onMouseMove={stopPropagation}
          onMouseUp={stopPropagation}
          onMouseLeave={stopPropagation}
        >
          <Stack direction="column" sx={{ marginTop: 1 }}>
            <TextField
              type="number"
              id="canvasLeft"
              margin="dense"
              value={inputLeft}
              onChange={handleLeftChange}
              onBlur={handleLeftBlur}
              onInput={handleLeftStep}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">px</InputAdornment>
                ),
                sx: {
                  bgcolor: "#fff",
                  height: 36,
                  width: 122,
                  paddingTop: 1,
                  marginRight: 1,
                },
                inputProps: { step: 1 }, // Step 1px for fine control
              }}
              label="Left"
              sx={{
                "& .MuiInputLabel-root": {
                  fontFamily: "Roboto",
                  fontWeight: 500,
                  marginTop: 1,
                },
              }}
              variant="outlined"
              inputProps={{ "aria-label": "Left position in pixels" }}
            />
            <TextField
              type="number"
              id="canvasTop"
              margin="dense"
              value={inputTop}
              onChange={handleTopChange}
              onBlur={handleTopBlur}
              onInput={handleTopStep}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">px</InputAdornment>
                ),
                sx: {
                  bgcolor: "#fff",
                  height: 36,
                  width: 122,
                  paddingTop: 1,
                  marginRight: 1,
                },
                inputProps: { step: 1 }, // Step 1px for fine control
              }}
              label="Top"
              sx={{
                "& .MuiInputLabel-root": {
                  fontFamily: "Roboto",
                  fontWeight: 500,
                  marginTop: 1,
                },
              }}
              variant="outlined"
              inputProps={{ "aria-label": "Top position in pixels" }}
            />
          </Stack>
        </DialogContent>
        <DialogActions
          onMouseDown={stopPropagation}
          onMouseMove={stopPropagation}
          onMouseUp={stopPropagation}
          onMouseLeave={stopPropagation}
        >
          <Button
            onClick={handleCancel}
            color="secondary"
            aria-label="Cancel move changes"
          >
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            color="primary"
            aria-label="Apply move changes"
          >
            Apply Move
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
);

export default MoveDraggable;
